import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { setupAxiosInterceptors } from '../axiosSetup';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import CustomNavbar from '../CustomNavbar';
import 'moment/locale/de';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './css/Calendar.css';
import Select from 'react-select';
import ZREventTypes from './zrEventTypes';
import cfg from '../../config';



moment.locale('de');
const localizer = momentLocalizer(moment);

const typeColors = [
  '#0d6efd', '#dc3545', '#198754', '#fd7e14',
  '#6f42c1', '#20c997', '#e83e8c', '#795548',
];

const customStyles = {
  control: (provided) => ({
    ...provided,
    minHeight: '38px',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
};






const loadColorMap = () => {
  const stored = localStorage.getItem('eventTypeColors');
  return stored ? JSON.parse(stored) : {};
};

const saveColorMap = (map) => {
  localStorage.setItem('eventTypeColors', JSON.stringify(map));
};

const CustomToolbar = ({ label, onNavigate }) => {
  const handlePrev = () => onNavigate('PREV');
  const handleNext = () => onNavigate('NEXT');
  const handleToday = () => onNavigate('TODAY');

  const monthYear = moment(label, 'MMMM YYYY').format('MMMM YYYY');

  return (
    <div className="d-flex justify-content-between align-items-center mb-2 p-2" style={{ backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
      <div>
        <button className="btn btn-sm btn-outline-secondary me-2" onClick={handleToday}>Heute</button>
        <button className="btn btn-sm btn-outline-secondary me-2" onClick={handlePrev}>‹</button>
        <button className="btn btn-sm btn-outline-secondary" onClick={handleNext}>›</button>
      </div>
      <h5 className="m-0 text-capitalize text-dark">{monthYear}</h5>
    </div>
  );
};

const ZRCalendar = () => {
  const [events, setEvents] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiMessage, setApiMessage] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [modalMode, setModalMode] = useState('view'); // 'view' | 'add' | 'edit'
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [members, setMembers] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    time: '',
    description: '',
    typeId: '',
    memberID: '',    // für Mitgliedsauswahl
    sendPush: false, // Push-Checkbox
    forZunftrat: false,
  });
  

    useEffect(() => {
      const fetchMembers = async () => {
        try {
          const token = localStorage.getItem("authToken");
          const response = await api.get("/api/member-zunftrat", {
            headers: { Authorization: `Bearer ${token}` },
          });
          setMembers(response.data || []);
        } catch (err) {
          console.error("Fehler beim Abrufen der Mitglieder:", err);
        }
      };
      fetchMembers();
    }, []);

  const navigate = useNavigate();
  const colorMapRef = useRef(loadColorMap());

  const getTypeColor = (typeId) => {
    if (!typeId) return '#0d6efd';
    if (!colorMapRef.current[typeId]) {
      const index = Object.keys(colorMapRef.current).length % typeColors.length;
      colorMapRef.current[typeId] = typeColors[index];
      saveColorMap(colorMapRef.current);
    }
    return colorMapRef.current[typeId];
  };

  useEffect(() => {
    if (apiMessage) {
      const timer = setTimeout(() => setApiMessage(null), 5000); // 5 Sekunden
      return () => clearTimeout(timer);
    }
  }, [apiMessage]);

  useEffect(() => {
    setupAxiosInterceptors(navigate);
    fetchEventTypes();
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const fetchEventTypes = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await api.get('/api/event-types', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEventTypes(response.data || []);
    } catch (err) {
      console.error('Fehler beim Laden der Event-Typen:', err);
    }
  };

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('authToken');
      const response = await api.get('/api/events-zunftrat', {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Beim Laden der Events
      const formattedEvents = (response.data || []).map(e => {
        const startDate = e.date; // z.B. "2025-11-10"
        const time = e.time || '00:00'; // z.B. "14:30"
      
        // sicherer Moment-Parse in lokaler Zeit
        const start = moment(`${startDate} ${time}`, 'YYYY-MM-DD HH:mm').toDate();
      
        return {
          ...e,
          start,
          end: start, // wenn Dauer = 0
        };
      });
      
      // Beim Speichern eines neuen/editierten Events
      const payload = {
        title: formData.title,
        description: formData.description || '',
        date: formData.date,
        time: formData.time || null,
        typeId: formData.typeId,
        memberID: formData.memberID || null, // neues Feld
        forZunftrat: formData.forZunftrat || null,
        sendPush: formData.sendPush || false, // neues Feld
        sendMail: formData.sendMail || false
      };
      

      formattedEvents.forEach(ev => getTypeColor(ev.typeId));
      setEvents(formattedEvents);
    } catch (err) {
      console.error('Fehler beim Laden der Termine:', err);
      setError(err.message || 'Fehler beim Laden der Termine.');
    } finally {
      setLoading(false);
    }
  };

  const eventStyleGetter = (event) => ({
    style: {
      backgroundColor: getTypeColor(event.typeId),
      color: 'white',
      borderRadius: '5px',
      border: 'none',
      padding: '2px 5px',
    },
  });

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    setFormData({
      title: event.title,
      date: moment(event.start).format('YYYY-MM-DD'),
      time: moment(event.start).format('HH:mm') !== '00:00' ? moment(event.start).format('HH:mm') : '', // nur anzeigen, wenn nicht 00:00
      description: event.description || '',
      typeId: event.typeId,
      forZunftrat: event.forZunftrat
    });
    
    setModalMode('view');
    setApiMessage(null);
  };
  

  const handleCloseModal = () => {
    setSelectedEvent(null);
    setModalMode('view');
    setFormData({ title: '', date: '', time:'', description: '', typeId: '', memberID: null, sendPush: false, sendMail: false, forZunftrat:false  });
    // setApiMessage(null);  <-- entfernt
  };
  

  const handleAddClick = () => {
    setFormData({ title: '', date: '', time:'', description: '', typeId: '', memberID: null, sendPush: false, sendMail: false  });
    setModalMode('add');
    setSelectedEvent(null);
    setApiMessage(null);
  };

  const handleEditClick = () => {
    if (!selectedEvent) return;
  
    console.log("Mein ausgewähltes Event: ", selectedEvent)

    // Umzugstermine werden im eigenen Bereich gepflegt, nicht hier.
    // Erkannt am Kennzeichen der Terminart, nicht an ihrem Namen — sonst
    // fiele die Sperre aus, sobald jemand den Typ umbenennt.
    if (eventTypes.find(t => t.id === selectedEvent.typeId)?.isMove) return;


  
    setFormData({
      title: selectedEvent.title || '',
      date: moment(selectedEvent.start).format('YYYY-MM-DD'),
      time: moment(selectedEvent.start).format('HH:mm') !== '00:00' ? moment(selectedEvent.start).format('HH:mm') : '',
      description: selectedEvent.description || '',
      typeId: selectedEvent.typeId || '',
      memberID: selectedEvent.memberID || '',   // Mitglied vorauswählen
      sendPush: selectedEvent.sendPush || false, // Checkbox vorauswählen
      sendMail: selectedEvent.sendMail || false,
      forZunftrat: selectedEvent.forZunftrat
    });
  
    setModalMode('edit');
    setApiMessage(null);
  };
  
  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  const handleConfirmDelete = async () => {
    if (!selectedEvent) return;

    try {
      const token = localStorage.getItem('authToken');
      const resp = await api.delete(`/api/events/${selectedEvent.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setApiMessage({ type: 'success', text: resp.data?.message || 'Termin gelöscht' });
      await fetchEvents();
      handleCloseModal();
    } catch (err) {
      console.error('Fehler beim Löschen des Termins:', err);
      setApiMessage({ type: 'danger', text: err.response?.data?.message || err.message || 'Fehler beim Löschen' });
    } finally {
      setShowDeleteConfirm(false);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setApiMessage(null);
  
    // Pflichtfelder prüfen
    if (!formData.title || !formData.date || !formData.typeId) {
      setApiMessage({ type: 'danger', text: 'Bitte Titel, Datum und Typ angeben.' });
      return;
    }
  
    if (eventTypes.find(t => t.id === formData.typeId)?.isMove) {
      setApiMessage({ type: 'danger', text: `${cfg.eventTermPlural} müssen im eigenen Bereich erstellt werden.` });
      return;
    }
  
    try {
      const token = localStorage.getItem('authToken');
  
      // Payload für API
      const payload = {
        title: formData.title,
        description: formData.description || '',
        date: formData.date,
        time: formData.time || null,
        typeId: formData.typeId,
        memberID: formData.memberID || null,
        sendPush: formData.sendPush || false,
        sendMail: formData.sendMail || false,
        forZunftrat: formData.forZunftrat || null
      };
  
      let updatedEvent = null;
  
      if (modalMode === 'add') {
        const resp = await api.post('/api/events', payload, { headers: { Authorization: `Bearer ${token}` } });
        updatedEvent = resp.data; // Backend sollte die neue Event-ID zurückgeben
  
        // Event direkt lokal zum State hinzufügen
        const startDate = updatedEvent.date;
        const time = updatedEvent.time || '00:00';
        const [hours, minutes] = time.split(':');
        const start = new Date(startDate);
        start.setHours(parseInt(hours), parseInt(minutes));
  
        setEvents(prev => [...prev, { ...updatedEvent, start, end: start }]);
        setApiMessage({ type: 'success', text: 'Termin erstellt.' });
      } 
      else if (modalMode === 'edit' && selectedEvent) {
        await api.put(`/api/events/${selectedEvent.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        
        // Event im State direkt aktualisieren
        setEvents(prev => prev.map(ev => {
          if (ev.id === selectedEvent.id) {
            const startDate = payload.date;
            const time = payload.time || '00:00';
            const [hours, minutes] = time.split(':');
            const start = new Date(startDate);
            start.setHours(parseInt(hours), parseInt(minutes));
            return { ...ev, ...payload, start, end: start };
          }
          return ev;
        }));
  
        setApiMessage({ type: 'success', text: 'Termin gespeichert.' });
      }
  
      handleCloseModal(); // Modal schließen, nachdem der State aktualisiert ist
    } catch (err) {
      console.error('Fehler beim Speichern des Termins:', err);
      setApiMessage({ type: 'danger', text: err.response?.data?.message || err.message || 'Fehler beim Speichern' });
    }
  };
  

  const formatMoveField = (field) => {
    if (!field || field.value === null || field.value === undefined) return null;
    switch (field.dataType) {
      case 'boolean':   return field.value ? 'Ja' : 'Nein';
      case 'date':      return moment(field.value).isValid() ? moment(field.value).format('DD.MM.YYYY') : String(field.value);
      case 'time':      return moment(String(field.value), 'HH:mm:ss').isValid() ? moment(String(field.value), 'HH:mm:ss').format('HH:mm') + ' Uhr' : String(field.value);
      case 'datetime':  return moment(field.value).isValid() ? moment(field.value).format('DD.MM.YYYY HH:mm') + ' Uhr' : String(field.value);
      case 'departures': return null; // wird separat gerendert
      default:          return field.value != null ? String(field.value) : null;
    }
  };

  const dropdownTypes = eventTypes.filter(t => !t.isMove);

  const allTypesMap = {};
  eventTypes.forEach(t => allTypesMap[t.id] = t.name);
  events.forEach(e => { if (e.typeId && !allTypesMap[e.typeId]) allTypesMap[e.typeId] = e.typeName || 'Unbekannt'; });
  const allTypes = Object.entries(allTypesMap);

  return (
    <>
      <CustomNavbar />
      <div className="container mt-4">
        <h3>Kalender ({cfg.adminRoleName})</h3>

        <div className="mb-3 d-flex gap-2">
          <button className="btn btn-primary" onClick={handleAddClick}>Termin hinzufügen</button>
        </div>

        {apiMessage && <div className={`alert alert-${apiMessage.type} mt-2`} role="alert">{apiMessage.text}</div>}
        {error && <p className="text-danger">{error}</p>}

        {loading ? <p>Lade Termine...</p> : (
          <>
          <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{ height: 600 }}
          date={selectedDate}
          onNavigate={(date, view) => setSelectedDate(date)} // immer Datum setzen
          onDrillDown={(date, view) => {
            setSelectedDate(date); // Datum auf angeklickten Tag setzen
            // den View-Change nicht blockieren
          }}
          eventPropGetter={eventStyleGetter}
          onSelectEvent={handleSelectEvent}
          popup
          views={['month', 'week', 'day']}
          defaultView="month"
          components={{ toolbar: CustomToolbar }}
          messages={{
            showMore: total => `+${total} weitere`,
            month: 'Monat',
            week: 'Woche',
            day: 'Tag',
            today: 'Heute',
            previous: 'Zurück',
            next: 'Weiter',
          }}
        />



            {allTypes.length > 0 && (
              <div className="mt-3">
                <h6>Legende:</h6>
                <div className="d-flex flex-wrap gap-2">
                  {allTypes.map(([id, name]) => (
                    <span key={id} className="badge" style={{ backgroundColor: getTypeColor(id), color: 'white', padding: '5px 10px', borderRadius: '5px', fontSize: '0.85rem' }}>
                      {name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            
            {(selectedEvent || modalMode === 'add') && (
              <div className="modal-overlay" onClick={handleCloseModal}>
                <div className="modal-box animate-in" onClick={e => e.stopPropagation()}>
                {console.log('modalMode:', modalMode)}
                  {/* === Close Button === */}
                  <button className="modal-close" onClick={handleCloseModal}>
                    &times;
                  </button>

                  {console.log('Modal FormData / SelectedEvent:', formData, selectedEvent)}

                  {/* === Modal Titel === */}
                  <h3 style={{ color: '#000' }} className="text-xl font-semibold mb-4">
                    {modalMode === 'add'
                      ? 'Neuen Termin hinzufügen'
                      : modalMode === 'edit'
                      ? 'Termin bearbeiten'
                      : selectedEvent?.title}
                  </h3>

                  <div className="modal-section" style={{ color: '#000' }}>
                    {(modalMode === 'add' || modalMode === 'edit') ? (
                      <form onSubmit={handleFormSubmit}>
                        {/* === Titel === */}
                        <div className="mb-3">
                          <label className="form-label">Titel</label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.title}
                            onChange={e => setFormData({ ...formData, title: e.target.value })}
                            required
                          />
                        </div>

                        {/* === Datum === */}
                        <div className="mb-3">
                          <label className="form-label">Datum</label>
                          <input
                            type="date"
                            className="form-control"
                            value={formData.date}
                            onChange={e => setFormData({ ...formData, date: e.target.value })}
                            required
                          />
                        </div>

                        {/* === Uhrzeit === */}
                        <div className="mb-3">
                          <label className="form-label">Uhrzeit</label>
                          <input
                            type="time"
                            className="form-control"
                            value={formData.time || ''}
                            onChange={e => setFormData({ ...formData, time: e.target.value })}
                          />
                        </div>

                        {/* === Typ === */}
                        <div className="mb-3">
                          <label className="form-label">Typ</label>
                          <select
                            className="form-select"
                            value={formData.typeId || ''}
                            onChange={e => setFormData({ ...formData, typeId: e.target.value })}
                            required
                          >
                            <option value="">-- Bitte wählen --</option>
                            {dropdownTypes.map((t, i) => (
                              <option key={i} value={t.id}>{t.name}</option>
                            ))}
                          </select>
                          {dropdownTypes.length === 0 && (
                            <div className="form-text text-warning">
                              Es ist noch kein Termin-Typ angelegt. Neue Typen kannst du weiter unten
                              auf dieser Seite unter „Termin-Typen" hinzufügen. Die Terminart für
                              {' '}{cfg.eventTermPlural} erscheint hier nicht – die werden in ihrem
                              eigenen Bereich angelegt.
                            </div>
                          )}
                        </div>

                        {/* === Beschreibung === */}
                        <div className="mb-3">
                          <label className="form-label">Beschreibung</label>
                          <textarea
                            className="form-control"
                            style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word', color: '#000' }}
                            value={formData.description || ''}
                            onChange={e => setFormData({ ...formData, description: e.target.value })}
                          />
                        </div>

                        {/* === Checkbox: Für alle Zunfträte === */}
                        <div className="form-check mb-3">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="forZunftrat"
                            checked={formData.forZunftrat || false}
                            onChange={e => setFormData({ ...formData, forZunftrat: e.target.checked })}
                          />
                          <label className="form-check-label" htmlFor="forZunftrat">
                            Termin ist für alle Zunfträte
                          </label>
                        </div>

                        {/* === Mitglied auswählen === */}
                        <div className="mb-3">
                          <label className="form-label">{cfg.memberTerm} auswählen</label>
                          <Select
                            options={members.map(m => ({
                              value: m.ID,
                              label: `${m.firstName} ${m.lastName}`,
                            }))}
                            value={
                              members.find(m => m.ID === formData.memberID)
                                ? {
                                    value: formData.memberID,
                                    label: `${members.find(m => m.ID === formData.memberID).firstName} ${members.find(m => m.ID === formData.memberID).lastName}`,
                                  }
                                : null
                            }
                            onChange={selected => setFormData({ ...formData, memberID: selected?.value || '' })}
                            isSearchable
                            placeholder={`${cfg.memberTerm} auswählen...`}
                            styles={customStyles}
                          />
                        </div>

                        {/* === Push / Mail === */}
                        <div className="form-check mb-3">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="sendPush"
                            checked={formData.sendPush || false}
                            onChange={e => setFormData({ ...formData, sendPush: e.target.checked })}
                          />
                          <label className="form-check-label" htmlFor="sendPush">
                            Push-Benachrichtigung schicken?
                          </label>
                        </div>

                        <div className="form-check mb-3">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="sendMail"
                            checked={formData.sendMail || false}
                            onChange={e => setFormData({ ...formData, sendMail: e.target.checked })}
                          />
                          <label className="form-check-label" htmlFor="sendMail">
                            per E-Mail benachrichtigen?
                          </label>
                          <br />
                          <small className="form-text text-muted">
                            Die E-Mail kann nur an eine ausgewählte Person versendet werden.
                          </small>
                        </div>

                        {/* === Buttons === */}
                        <div className="d-flex gap-2 mt-3">
                          <button type="submit" className="btn btn-primary">Speichern</button>
                          <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                            Abbrechen
                          </button>
                        </div>
                      </form>
                    ) : (
                      <>
                        {/* === Anzeige-Modus === */}
                        
                        <p><strong>Datum:</strong> {moment(selectedEvent.start).format('DD.MM.YYYY')}</p>

                        {selectedEvent.typeName && (
                          <p>
                            <strong>Typ:</strong>{' '}
                            <span
                              style={{
                                color: '#000',
                                backgroundColor: getTypeColor(selectedEvent.typeId),
                                padding: '2px 6px',
                                borderRadius: '4px',
                              }}
                            >
                              {selectedEvent.typeName}
                            </span>
                          </p>
                        )}

                        {selectedEvent?.start && moment(selectedEvent.start).format('HH:mm') !== '00:00' && (
                          <p><strong>Uhrzeit:</strong> {moment(selectedEvent.start).format('HH:mm')} Uhr</p>
                        )}

                        {selectedEvent.description && (
                          <p><strong>Beschreibung:</strong> {selectedEvent.description}</p>
                        )}

                        {selectedEvent.memberName && (
                          <p><strong>Person:</strong> {selectedEvent.memberName}</p>
                        )}

                        {selectedEvent.forZunftrat === 1 && (
                          <div className="alert alert-info mt-3 p-2">
                            <i className="bi bi-lock-fill me-2"></i>
                            Dieser Termin ist nur für {cfg.adminRoleName} sichtbar.
                          </div>
                        )}

                        {selectedEvent?.isMove && selectedEvent.move?.fields?.length > 0 && (
                          <>
                            <hr />
                            <h6><strong>Details zur Veranstaltung:</strong></h6>
                            <ul className="list-unstyled mb-0">
                              {selectedEvent.move.fields.map((f, i) => {
                                if (f.dataType === 'departures') {
                                  if (!Array.isArray(f.value) || f.value.length === 0) return null;
                                  return f.value.map((d, j) => (
                                    <li key={`dep-${j}`}><strong>Abfahrt {d.label}:</strong> {moment(String(d.time), 'HH:mm:ss').format('HH:mm')} Uhr</li>
                                  ));
                                }
                                const display = formatMoveField(f);
                                if (display == null) return null;
                                return <li key={i}><strong>{f.frontendName}:</strong> {display}</li>;
                              })}
                            </ul>
                          </>
                        )}
                      </>
                    )}
                  </div>

                 {/* === Footer === */}
                  {modalMode === 'view' && selectedEvent && (
                    <div className="modal-footer mt-4 d-flex flex-wrap gap-2">
                      {!selectedEvent.isMove && (
                        <button className="btn btn-warning" onClick={handleEditClick}>
                          Bearbeiten
                        </button>
                      )}
                      {!selectedEvent.isMove && (
                        <button className="btn btn-danger" onClick={handleDeleteClick}>
                          Löschen
                        </button>
                      )}
                      <button className="btn btn-secondary" onClick={handleCloseModal}>
                        Schließen
                      </button>
                    </div>
                  )}

                </div>
              </div>
            )}



            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
              <div className="modal-overlay" onClick={handleCancelDelete}>
                <div
                  className="modal-box animate-in"
                  onClick={e => e.stopPropagation()}
                >
                  {/* === Header === */}
                  <div className="modal-header">
                    <h5 className="modal-title text-dark">Termin löschen</h5>
                    <button type="button" className="btn-close" onClick={handleCancelDelete}></button>
                  </div>

                  {/* === Body === */}
                  <div className="modal-section text-dark">
                    <p>
                      Willst du den Termin <strong>{selectedEvent?.title}</strong> wirklich löschen?
                    </p>
                  </div>

                  {/* === Footer === */}
                  <div className="modal-footer d-flex flex-wrap gap-2 mt-3">
                    <button className="btn btn-secondary" onClick={handleCancelDelete}>
                      Abbrechen
                    </button>
                    <button className="btn btn-danger" onClick={handleConfirmDelete}>
                      Löschen
                    </button>
                  </div>
                </div>
              </div>
            )}

          </>
        )}
        <br/>
        <br/>
        <ZREventTypes>

        </ZREventTypes>
        <br/>
        <br/>
      </div>
    </>
  );
};

export default ZRCalendar;
